import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CatalogItem,
  AreaSceneItem,
  DisplayEventItem,
  DisplayTemplateDocument,
  DisplayTemplateItem,
  GameVariantItem,
} from "@shared/index";
import { DISPLAY_TEMPLATE_SCHEMA_VERSION } from "@shared/index";
import { api, ApiRequestError } from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Input,
  LoadingBlock,
  Select,
} from "@/components/ui";
import { useToast } from "@/lib/toast";
import { copy, useI18n } from "@/lib/i18n";
import type { ProductRow } from "../admin/shared";
import {
  DisplayTemplateEditor,
  GameVariantEditor,
} from "./DisplayRuntimeEditors";
const AreaEditorWorkspace = lazy(() =>
  import("./AreaEditorWorkspace").then((module) => ({
    default: module.AreaEditorWorkspace,
  })),
);
import "./DisplayRuntimeWorkspace.css";

interface StudioEndpoint {
  id: string;
  accountId?: string;
  accountName?: string;
  name: string;
  kind: string;
  status: string;
}
type StudioProduct = Pick<
  ProductRow,
  "id" | "name" | "slug" | "delivery" | "category" | "published"
> & { entitled?: boolean };
type WorkflowTab = "templates" | "areas" | "variants" | "events" | "live";
interface EventDraftEntry {
  variantId: string;
  enabled: boolean;
  repeatCount: number;
  roundLimit: string;
  durationSeconds: string;
}

const WORKFLOW_STEPS: Array<{
  id: WorkflowTab;
  tr: string;
  en: string;
  trDescription: string;
  enDescription: string;
}> = [
  {
    id: "templates",
    tr: "Ekranı tasarla",
    en: "Design the screen",
    trDescription: "QR, skor, logo ve yayın katmanlarını yerleştirin.",
    enDescription: "Arrange QR, score, logo, and broadcast layers.",
  },
  {
    id: "areas",
    tr: "Alanı tasarla",
    en: "Design the area",
    trDescription: "3B oyun mekânı, kamera ve güvenli fizik nesnelerini hazırlayın.",
    enDescription: "Prepare the 3D world, camera, and safe physics objects.",
  },
  {
    id: "variants",
    tr: "Oyunu özelleştir",
    en: "Customize the game",
    trDescription: "Ana oyunu ekran tasarımı ve marka ile birleştirin.",
    enDescription: "Combine a core game with a screen design and brand.",
  },
  {
    id: "events",
    tr: "Etkinliği hazırla",
    en: "Prepare the event",
    trDescription: "Oyun sırasını, zamanı ve çalışma biçimini belirleyin.",
    enDescription: "Set the game order, timing, and operating mode.",
  },
  {
    id: "live",
    tr: "Cihaza yayınla",
    en: "Publish to device",
    trDescription: "Yayını başlatın ve gerektiğinde canlı yönetin.",
    enDescription: "Start the broadcast and control it live when needed.",
  },
];

function localDateTime(hours: number) {
  const date = new Date(Date.now() + hours * 60 * 60 * 1000);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function defaultTemplate(): DisplayTemplateDocument {
  return {
    protocolVersion: DISPLAY_TEMPLATE_SCHEMA_VERSION,
    viewport: { width: 1920, height: 1080, safeAreaPercent: 3 },
    theme: {
      background: "#060817",
      foreground: "#f8f8ff",
      accent: "#6658e8",
      radius: 28,
    },
    layers: [
      {
        id: "game",
        kind: "game",
        x: 0,
        y: 0,
        width: 100,
        height: 100,
        zIndex: 0,
        visible: true,
        locked: true,
        settings: { fit: "cover" },
      },
      {
        id: "branding",
        kind: "branding",
        x: 3,
        y: 3,
        width: 20,
        height: 8,
        zIndex: 10,
        visible: true,
        locked: false,
        settings: {},
      },
      {
        id: "logo",
        kind: "logo",
        x: 3,
        y: 3,
        width: 18,
        height: 9,
        zIndex: 11,
        visible: false,
        locked: false,
        settings: { fit: "contain" },
      },
      {
        id: "lobby",
        kind: "lobby",
        x: 10,
        y: 15,
        width: 80,
        height: 70,
        zIndex: 30,
        visible: true,
        locked: false,
        settings: { automaticClose: true },
      },
      {
        id: "players",
        kind: "players",
        x: 12,
        y: 63,
        width: 58,
        height: 17,
        zIndex: 35,
        visible: true,
        locked: false,
        settings: {},
      },
      {
        id: "qr",
        kind: "qr",
        x: 73,
        y: 5,
        width: 24,
        height: 34,
        zIndex: 40,
        visible: true,
        locked: false,
        settings: { errorCorrection: "L", quietZone: 4 },
      },
      {
        id: "score",
        kind: "score",
        x: 3,
        y: 14,
        width: 16,
        height: 11,
        zIndex: 24,
        visible: true,
        locked: false,
        settings: { sampleValue: "2.450" },
      },
      {
        id: "timer",
        kind: "timer",
        x: 82,
        y: 42,
        width: 14,
        height: 10,
        zIndex: 24,
        visible: true,
        locked: false,
        settings: { sampleValue: "00:42" },
      },
      {
        id: "results",
        kind: "results",
        x: 10,
        y: 15,
        width: 80,
        height: 70,
        zIndex: 30,
        visible: true,
        locked: false,
        settings: {},
      },
      {
        id: "voting",
        kind: "voting",
        x: 10,
        y: 15,
        width: 80,
        height: 70,
        zIndex: 30,
        visible: true,
        locked: false,
        settings: {},
      },
      {
        id: "advertising",
        kind: "ad",
        x: 3,
        y: 86,
        width: 94,
        height: 11,
        zIndex: 20,
        visible: true,
        locked: false,
        settings: { consentRequired: true },
      },
      {
        id: "sponsor",
        kind: "sponsor",
        x: 66,
        y: 3,
        width: 28,
        height: 10,
        zIndex: 21,
        visible: false,
        locked: false,
        settings: { consentRequired: true },
      },
    ],
  };
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiRequestError ? error.message : fallback;
}

export default function DisplayRuntimeWorkspace({
  audience,
}: {
  audience: "admin" | "customer";
}) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<WorkflowTab>("templates");
  const [endpointId, setEndpointId] = useState("");
  const [templateName, setTemplateName] = useState("Ana LED ekran tasarımı");
  const [variantName, setVariantName] = useState("Sunum oyunu");
  const [templateId, setTemplateId] = useState("");
  const [productId, setProductId] = useState("");
  const [areaSceneId, setAreaSceneId] = useState("");
  const [eventName, setEventName] = useState("Canlı etkinlik");
  const [startsAt, setStartsAt] = useState(localDateTime(1));
  const [endsAt, setEndsAt] = useState(localDateTime(4));
  const [controlMode, setControlMode] = useState<
    "automatic" | "manual" | "hybrid"
  >("hybrid");
  const [rotationMode, setRotationMode] = useState<
    "sequential" | "shuffle" | "vote"
  >("vote");
  const [loop, setLoop] = useState(true);
  const [eventEntries, setEventEntries] = useState<EventDraftEntry[]>([]);
  const [editingTemplateId, setEditingTemplateId] = useState("");
  const [editingVariantId, setEditingVariantId] = useState("");
  const [showTemplateComposer, setShowTemplateComposer] = useState(false);

  const source = useQuery({
    queryKey: ["display-runtime", "source", audience],
    queryFn: async () => {
      if (audience === "admin") {
        const [operations, products] = await Promise.all([
          api.get<{ endpoints: StudioEndpoint[] }>("/admin/operations"),
          api.get<{ items: ProductRow[] }>("/admin/products", {
            pageSize: 100,
          }),
        ]);
        return {
          endpoints: operations.endpoints,
          products: products.items as StudioProduct[],
        };
      }
      const [endpoints, products] = await Promise.all([
        api.get<{ items: StudioEndpoint[] }>("/endpoints"),
        api.get<{ items: CatalogItem[] }>("/catalog", { ownedOnly: true }),
      ]);
      return {
        endpoints: endpoints.items,
        products: products.items.map((item) => ({
          ...item,
          published: true,
        })) as StudioProduct[],
      };
    },
  });
  const endpoints = (source.data?.endpoints ?? []).filter(
    (item) => item.kind === "led-screen",
  );
  const selectedEndpoint = endpoints.find((item) => item.id === endpointId);
  const accountId =
    audience === "admin" ? selectedEndpoint?.accountId : undefined;
  const accountQuery = accountId ? { accountId } : undefined;

  useEffect(() => {
    if (!endpointId && endpoints[0]) setEndpointId(endpoints[0].id);
  }, [endpointId, endpoints]);

  const templates = useQuery({
    queryKey: ["display-runtime", "templates", accountId ?? "self"],
    queryFn: () =>
      api.get<{ items: DisplayTemplateItem[] }>(
        "/display/templates",
        accountId ? { accountId } : undefined,
      ),
    enabled: audience === "customer" || Boolean(accountId),
  });
  const variants = useQuery({
    queryKey: ["display-runtime", "variants", accountId ?? "self"],
    queryFn: () =>
      api.get<{ items: GameVariantItem[] }>(
        "/display/variants",
        accountId ? { accountId } : undefined,
      ),
    enabled: audience === "customer" || Boolean(accountId),
  });
  const areaScenes = useQuery({
    queryKey: ["area-scenes", accountId ?? "self"],
    queryFn: () =>
      api.get<{ items: AreaSceneItem[] }>(
        "/area/scenes",
        accountId ? { accountId } : undefined,
      ),
    enabled: audience === "customer" || Boolean(accountId),
  });
  const events = useQuery({
    queryKey: ["display-runtime", "events", accountId ?? "self"],
    queryFn: () =>
      api.get<{ items: DisplayEventItem[] }>(
        "/display/events",
        accountId ? { accountId } : undefined,
      ),
    enabled: audience === "customer" || Boolean(accountId),
    refetchInterval: tab === "live" ? 5000 : false,
  });

  const ownedProducts = useMemo(
    () =>
      (source.data?.products ?? []).filter(
        (item) =>
          item.delivery === "desktop-install" &&
          item.published &&
          item.entitled !== false,
      ),
    [source.data],
  );
  const publishedTemplates = (templates.data?.items ?? []).filter(
    (item) => item.status === "published",
  );
  const publishedVariants = (variants.data?.items ?? []).filter(
    (item) => item.status === "published",
  );

  useEffect(() => {
    if (!templateId && publishedTemplates[0])
      setTemplateId(publishedTemplates[0].id);
    if (!productId && ownedProducts[0]) setProductId(ownedProducts[0].id);
  }, [ownedProducts, productId, publishedTemplates, templateId]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["display-runtime", "templates"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["display-runtime", "variants"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["display-runtime", "events"],
      }),
      queryClient.invalidateQueries({ queryKey: ["area-scenes"] }),
    ]);

  const createTemplate = useMutation({
    mutationFn: () =>
      api.post<DisplayTemplateItem>("/display/templates", {
        ...(accountId ? { accountId } : {}),
        name: templateName,
        description:
          "QR, lobi, sonuç, oylama, reklam ve marka katmanlarını oyundan ayrı tutan ekran kabuğu.",
        document: defaultTemplate(),
      }),
    onSuccess: async (created) => {
      toast.success(
        c(
          "Ekran tasarımı taslak olarak oluşturuldu.",
          "Screen design created as a draft.",
        ),
      );
      await refresh();
      setShowTemplateComposer(false);
      setEditingTemplateId(created.id);
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c(
            "Ekran tasarımı oluşturulamadı.",
            "Screen design could not be created.",
          ),
        ),
      ),
  });
  const publishTemplate = useMutation({
    mutationFn: (id: string) =>
      api.patch(
        `/display/templates/${id}`,
        { status: "published" },
        accountQuery,
      ),
    onSuccess: refresh,
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c(
            "Ekran tasarımı yayınlanamadı.",
            "Screen design could not be published.",
          ),
        ),
      ),
  });
  const createVariant = useMutation({
    mutationFn: () =>
      api.post("/display/variants", {
        ...(accountId ? { accountId } : {}),
        name: variantName,
        coreProductId: productId,
        displayTemplateId: templateId,
        areaSceneId: areaSceneId || null,
        branding: {},
        advertising: { consentRequired: true },
        gameSettings: {},
      }),
    onSuccess: async () => {
      toast.success(
        c(
          "Etkinlik oyunu oluşturuldu; yayınlamadan önce ekran tasarımını ve oyunu kontrol edin.",
          "Event game created; review the screen design and game before publishing.",
        ),
      );
      await refresh();
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c(
            "Etkinlik oyunu oluşturulamadı.",
            "Event game could not be created.",
          ),
        ),
      ),
  });
  const publishVariant = useMutation({
    mutationFn: (id: string) =>
      api.patch(
        `/display/variants/${id}`,
        { status: "published" },
        accountQuery,
      ),
    onSuccess: refresh,
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c(
            "Etkinlik oyunu yayınlanamadı.",
            "Event game could not be published.",
          ),
        ),
      ),
  });
  const createEvent = useMutation({
    mutationFn: () =>
      api.post("/display/events", {
        ...(accountId ? { accountId } : {}),
        endpointId,
        name: eventName,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        controlMode,
        rotationMode,
        loop,
        entries: eventEntries.map((entry) => ({
          variantId: entry.variantId,
          enabled: entry.enabled,
          repeatCount: entry.repeatCount,
          roundLimit: entry.roundLimit ? Number(entry.roundLimit) : null,
          durationSeconds: entry.durationSeconds
            ? Number(entry.durationSeconds)
            : null,
        })),
      }),
    onSuccess: async () => {
      toast.success(
        c(
          "Etkinlik yayını planlandı ve CORTEX kuyruğuna hazır.",
          "Event broadcast scheduled and ready for CORTEX.",
        ),
      );
      setEventEntries([]);
      await refresh();
      setTab("live");
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c(
            "Etkinlik yayını planlanamadı.",
            "Event broadcast could not be scheduled.",
          ),
        ),
      ),
  });
  const control = useMutation({
    mutationFn: ({
      eventId,
      action,
      targetEntryId,
    }: {
      eventId: string;
      action: string;
      targetEntryId?: string;
    }) =>
      api.post(
        `/display/events/${eventId}/control`,
        {
          action,
          ...(targetEntryId ? { targetEntryId } : {}),
          idempotencyKey: crypto.randomUUID(),
        },
        accountQuery,
      ),
    onSuccess: refresh,
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          c("Komut uygulanamadı.", "Command could not be applied."),
        ),
      ),
  });

  if (source.isLoading)
    return (
      <LoadingBlock
        rows={6}
        label={c("Display Runtime hazırlanıyor", "Preparing Display Runtime")}
      />
    );
  if (source.isError)
    return (
      <EmptyState
        title={c("Çalışma alanı açılamadı", "Workspace could not be opened")}
        description={c(
          "Cihaz ve oyun verileri alınamadı.",
          "Device and game data could not be loaded.",
        )}
      />
    );
  if (!endpoints.length)
    return (
      <EmptyState
        title={c("LED ekran bulunamadı", "No LED screen found")}
        description={c(
          "Önce bir LED ekran uç noktası tanımlayın.",
          "Create a LED screen endpoint first.",
        )}
      />
    );

  return (
    <section
      className="display-workflow"
      aria-labelledby="display-workflow-title"
    >
      <header className="display-workflow__head">
        <div>
          <h2 id="display-workflow-title">
            {c("Ekran yayınınızı hazırlayın", "Prepare your screen broadcast")}
          </h2>
          <p>
            {c(
              "Tasarımı oluşturun, oyunu özelleştirin, etkinlik akışını hazırlayın ve seçtiğiniz cihaza yayınlayın.",
              "Create the design, customize the game, prepare the event flow, and publish it to the selected device.",
            )}
          </p>
        </div>
        <Field label={c("Çalışma ekranı", "Target screen")}>
          {({ id }) => (
            <Select
              id={id}
              value={endpointId}
              onChange={(event) => setEndpointId(event.target.value)}
            >
              {endpoints.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.accountName ? `${item.accountName} · ` : ""}
                  {item.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </header>
      <nav
        className="display-workflow__journey"
        aria-label={c(
          "Yayın hazırlama adımları",
          "Broadcast preparation steps",
        )}
      >
        {WORKFLOW_STEPS.map((step, index) => {
          const isActive = tab === step.id;
          const count =
            step.id === "templates"
              ? (templates.data?.items.length ?? 0)
              : step.id === "areas"
                ? (areaScenes.data?.items.length ?? 0)
              : step.id === "variants"
                ? (variants.data?.items.length ?? 0)
                : step.id === "live"
                  ? (events.data?.items.filter((item) =>
                      ["scheduled", "running", "paused"].includes(item.status),
                    ).length ?? 0)
                  : eventEntries.length;
          return (
            <button
              type="button"
              key={step.id}
              className={isActive ? "is-active" : ""}
              aria-current={isActive ? "step" : undefined}
              onClick={() => setTab(step.id)}
            >
              <b>{index + 1}</b>
              <span>
                <strong>{c(step.tr, step.en)}</strong>
                <small>{c(step.trDescription, step.enDescription)}</small>
              </span>
              {count > 0 ? <em>{count}</em> : null}
            </button>
          );
        })}
      </nav>

      {tab === "templates" ? (
        (templates.data?.items ?? []).find(
          (item) => item.id === editingTemplateId,
        ) ? (
          <DisplayTemplateEditor
            template={
              (templates.data?.items ?? []).find(
                (item) => item.id === editingTemplateId,
              )!
            }
            products={ownedProducts.map((item) => ({
              id: item.id,
              name: item.name,
              category: item.category,
            }))}
            endpoints={endpoints.map((item) => ({
              id: item.id,
              name: item.name,
            }))}
            {...(accountId ? { accountId } : {})}
            onClose={() => setEditingTemplateId("")}
            onSaved={refresh}
          />
        ) : (
          <div className="display-workflow__collection">
            <header className="display-workflow__section-head">
              <div>
                <h3>{c("Ekran tasarımlarım", "My screen designs")}</h3>
                <p>
                  {c(
                    "Oyundan bağımsız çalışan QR, lobi, skor, reklam ve marka düzenlerinizi yönetin.",
                    "Manage QR, lobby, score, advertising, and brand layouts that run independently from the game.",
                  )}
                </p>
              </div>
              <Button
                variant="primary"
                onClick={() => setShowTemplateComposer((current) => !current)}
                aria-expanded={showTemplateComposer}
              >
                {showTemplateComposer
                  ? c("Vazgeç", "Cancel")
                  : c("Yeni ekran tasarımı", "New screen design")}
              </Button>
            </header>
            {showTemplateComposer ? (
              <section
                className="display-workflow__create-panel"
                aria-labelledby="new-screen-design-title"
              >
                <div>
                  <h4 id="new-screen-design-title">
                    {c("Yeni tasarımınızı adlandırın", "Name your new design")}
                  </h4>
                  <p>
                    {c(
                      "Hazır başlangıç düzeni oyun alanını korur. Diğer bileşenleri editörde taşıyabilir veya kaldırabilirsiniz.",
                      "The starter layout protects the game area. You can move or remove other components in the editor.",
                    )}
                  </p>
                </div>
                <Field label={c("Tasarım adı", "Design name")}>
                  {({ id }) => (
                    <Input
                      id={id}
                      value={templateName}
                      onChange={(event) => setTemplateName(event.target.value)}
                    />
                  )}
                </Field>
                <Button
                  variant="primary"
                  loading={createTemplate.isPending}
                  disabled={templateName.trim().length < 2}
                  onClick={() => createTemplate.mutate()}
                >
                  {c("Oluştur ve düzenle", "Create and edit")}
                </Button>
              </section>
            ) : null}
            <div className="display-workflow__list">
              {(templates.data?.items ?? []).map((item) => (
                <article key={item.id}>
                  <div className="display-workflow__item-copy">
                    <Badge
                      tone={
                        item.publishedRevision !== item.revision
                          ? "warning"
                          : item.status === "published"
                            ? "success"
                            : "neutral"
                      }
                    >
                      {item.publishedRevision !== item.revision
                        ? c("Yayınlanmamış değişiklik", "Unpublished changes")
                        : item.status === "published"
                          ? c("Yayında", "Published")
                          : c("Taslak", "Draft")}
                    </Badge>
                    <h3>{item.name}</h3>
                    <p>
                      {item.document.layers.length} {c("katman", "layers")} ·{" "}
                      {c("Sürüm", "Revision")} {item.revision}
                    </p>
                  </div>
                  <div className="display-workflow__row-actions">
                    {item.publishedRevision !== item.revision ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => publishTemplate.mutate(item.id)}
                      >
                        {c("Yayınla", "Publish")}
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      onClick={() => setEditingTemplateId(item.id)}
                    >
                      {c("Tasarıma gir", "Open design")}
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )
      ) : null}

      {tab === "areas" ? (
        <Suspense fallback={<LoadingBlock rows={7} />}>
          <AreaEditorWorkspace
            {...(accountId ? { accountId } : {})}
            products={ownedProducts.map((item) => ({ id: item.id, name: item.name }))}
          />
        </Suspense>
      ) : null}

      {tab === "variants" ? (
        <>
          <div className="display-workflow__grid">
            <Card title={c("Yeni etkinlik oyunu", "New event game")}>
              <p className="display-workflow__hint">
                {c(
                  "Ana oyunu bir ekran tasarımıyla birleştirin. Marka ve sponsor ayarlarını sonraki ekranda düzenleyebilirsiniz.",
                  "Combine a core game with a screen design. Brand and sponsor settings can be edited next.",
                )}
              </p>
              <Field label={c("Etkinlikte görünen ad", "Event display name")}>
                {({ id }) => (
                  <Input
                    id={id}
                    value={variantName}
                    onChange={(event) => setVariantName(event.target.value)}
                  />
                )}
              </Field>
              <Field label={c("Ana oyun", "Core game")}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={productId}
                    onChange={(event) => setProductId(event.target.value)}
                  >
                    <option value="">{c("Oyun seçin", "Choose game")}</option>
                    {ownedProducts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={c("Ekran tasarımı", "Screen design")}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={templateId}
                    onChange={(event) => setTemplateId(event.target.value)}
                  >
                    <option value="">
                      {c(
                        "Yayınlanmış ekran tasarımı seçin",
                        "Choose a published screen design",
                      )}
                    </option>
                    {publishedTemplates.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={c("3B oyun alanı", "3D game area")}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={areaSceneId}
                    onChange={(event) => setAreaSceneId(event.target.value)}
                  >
                    <option value="">
                      {c("Varsayılan oyun alanı", "Default game area")}
                    </option>
                    {(areaScenes.data?.items ?? [])
                      .filter(
                        (scene) =>
                          scene.coreProductId === productId &&
                          scene.status === "published",
                      )
                      .map((scene) => (
                        <option key={scene.id} value={scene.id}>
                          {scene.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
              <Button
                variant="primary"
                disabled={
                  !productId || !templateId || variantName.trim().length < 2
                }
                loading={createVariant.isPending}
                onClick={() => createVariant.mutate()}
              >
                {c("Oyunu hazırla", "Prepare game")}
              </Button>
            </Card>
            <div className="display-workflow__list">
              {(variants.data?.items ?? []).map((item) => (
                <article key={item.id}>
                  <div>
                    <Badge
                      tone={item.status === "published" ? "success" : "warning"}
                    >
                      {item.status === "published"
                        ? c("Yayında", "Published")
                        : c("Taslak", "Draft")}
                    </Badge>
                    <h3>{item.name}</h3>
                    <p>
                      {item.coreProductName} · {item.displayTemplateName}
                      {item.areaSceneName ? ` · ${item.areaSceneName}` : ""} · r
                      {item.revision}
                    </p>
                  </div>
                  <div className="display-workflow__row-actions">
                    <Button
                      size="sm"
                      onClick={() => setEditingVariantId(item.id)}
                    >
                      {c("Özelleştir", "Customize")}
                    </Button>
                    {item.status === "draft" ? (
                      <Button
                        size="sm"
                        onClick={() => publishVariant.mutate(item.id)}
                      >
                        {c("Yayınla", "Publish")}
                      </Button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          </div>
          {(variants.data?.items ?? []).find(
            (item) => item.id === editingVariantId,
          ) ? (
            <GameVariantEditor
              variant={
                (variants.data?.items ?? []).find(
                  (item) => item.id === editingVariantId,
                )!
              }
              {...(accountId ? { accountId } : {})}
              audience={audience}
              areaScenes={areaScenes.data?.items ?? []}
              onSaved={refresh}
            />
          ) : null}
        </>
      ) : null}

      {tab === "events" ? (
        <div className="display-workflow__event">
          <Card title={c("Etkinlik yayın listesi", "Event broadcast list")}>
            <p className="display-workflow__hint">
              {c(
                "Yayınlanacak oyunları seçin; çalışma sırasını ve moderatör kontrolünü belirleyin.",
                "Choose the games to publish, then set their order and moderator control.",
              )}
            </p>
            <div className="display-workflow__form-grid">
              <Field label={c("Etkinlik adı", "Event name")}>
                {({ id }) => (
                  <Input
                    id={id}
                    value={eventName}
                    onChange={(event) => setEventName(event.target.value)}
                  />
                )}
              </Field>
              <Field label={c("Başlangıç", "Starts")}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="datetime-local"
                    value={startsAt}
                    onChange={(event) => setStartsAt(event.target.value)}
                  />
                )}
              </Field>
              <Field label={c("Bitiş", "Ends")}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="datetime-local"
                    value={endsAt}
                    onChange={(event) => setEndsAt(event.target.value)}
                  />
                )}
              </Field>
              <Field label={c("Yönetim", "Control")}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={controlMode}
                    onChange={(event) =>
                      setControlMode(event.target.value as typeof controlMode)
                    }
                  >
                    <option value="automatic">
                      {c("Otomatik", "Automatic")}
                    </option>
                    <option value="manual">
                      {c("Yalnız moderatör", "Moderator only")}
                    </option>
                    <option value="hybrid">
                      {c(
                        "Otomatik + moderatör müdahalesi",
                        "Automatic + moderator override",
                      )}
                    </option>
                  </Select>
                )}
              </Field>
              <Field label={c("Oyun seçimi", "Game selection")}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={rotationMode}
                    onChange={(event) =>
                      setRotationMode(event.target.value as typeof rotationMode)
                    }
                  >
                    <option value="sequential">
                      {c("Sırayla", "Sequential")}
                    </option>
                    <option value="shuffle">{c("Karışık", "Shuffle")}</option>
                    <option value="vote">
                      {c("Oyuncu oylaması", "Player voting")}
                    </option>
                  </Select>
                )}
              </Field>
              <Checkbox
                checked={loop}
                onChange={(event) => setLoop(event.target.checked)}
                label={c("Liste bitince başa dön", "Loop when the list ends")}
              />
            </div>
            <div className="display-workflow__variant-pool">
              <strong>{c("Yayınlanmış oyunlar", "Published games")}</strong>
              <div className="display-workflow__basket">
                {publishedVariants.map((item) => {
                  const selected = eventEntries.some(
                    (entry) => entry.variantId === item.id,
                  );
                  return (
                    <label key={item.id} className={selected ? "is-selected" : ""}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(event) =>
                          setEventEntries((current) =>
                            event.target.checked
                              ? [
                                  ...current,
                                  {
                                    variantId: item.id,
                                    enabled: true,
                                    repeatCount: 1,
                                    roundLimit: "",
                                    durationSeconds: "",
                                  },
                                ]
                              : current.filter(
                                  (entry) => entry.variantId !== item.id,
                                ),
                          )
                        }
                      />
                      <span>
                        <strong>{item.name}</strong>
                        <small>
                          {item.coreProductName} · {item.displayTemplateName}
                        </small>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            {eventEntries.length ? (
              <section className="display-workflow__playlist">
                <header>
                  <div>
                    <strong>{c("Oyun akışı", "Game flow")}</strong>
                    <small>
                      {c(
                        "Sırayı değiştirin; her oyun için tekrar, tur veya süre sınırı belirleyin.",
                        "Reorder games and set repeat, round, or duration limits for each one.",
                      )}
                    </small>
                  </div>
                  <Badge tone="neutral">
                    {eventEntries.length} {c("oyun", "games")}
                  </Badge>
                </header>
                <div className="display-workflow__playlist-list">
                  {eventEntries.map((entry, index) => {
                    const item = publishedVariants.find(
                      (variant) => variant.id === entry.variantId,
                    );
                    if (!item) return null;
                    const update = (patch: Partial<EventDraftEntry>) =>
                      setEventEntries((current) =>
                        current.map((candidate) =>
                          candidate.variantId === entry.variantId
                            ? { ...candidate, ...patch }
                            : candidate,
                        ),
                      );
                    const move = (offset: number) =>
                      setEventEntries((current) => {
                        const from = current.findIndex(
                          (candidate) => candidate.variantId === entry.variantId,
                        );
                        const to = from + offset;
                        if (from < 0 || to < 0 || to >= current.length)
                          return current;
                        const next = [...current];
                        [next[from], next[to]] = [next[to]!, next[from]!];
                        return next;
                      });
                    return (
                      <article key={entry.variantId}>
                        <div className="display-workflow__playlist-rank">
                          <b>{String(index + 1).padStart(2, "0")}</b>
                          <span>
                            <strong>{item.name}</strong>
                            <small>{item.coreProductName}</small>
                          </span>
                        </div>
                        <div className="display-workflow__playlist-order">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={index === 0}
                            onClick={() => move(-1)}
                            aria-label={c("Yukarı taşı", "Move up")}
                          >
                            ↑
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={index === eventEntries.length - 1}
                            onClick={() => move(1)}
                            aria-label={c("Aşağı taşı", "Move down")}
                          >
                            ↓
                          </Button>
                        </div>
                        <label>
                          <span>{c("Tekrar", "Repeats")}</span>
                          <Input
                            type="number"
                            min={1}
                            max={100}
                            value={entry.repeatCount}
                            onChange={(event) =>
                              update({
                                repeatCount: Math.min(
                                  100,
                                  Math.max(1, Number(event.target.value) || 1),
                                ),
                              })
                            }
                          />
                        </label>
                        <label>
                          <span>{c("Tur sınırı", "Round limit")}</span>
                          <Input
                            type="number"
                            min={1}
                            max={1000}
                            placeholder={c("Sınırsız", "Unlimited")}
                            value={entry.roundLimit}
                            onChange={(event) =>
                              update({ roundLimit: event.target.value })
                            }
                          />
                        </label>
                        <label>
                          <span>{c("Süre (sn)", "Duration (sec)")}</span>
                          <Input
                            type="number"
                            min={30}
                            max={86400}
                            placeholder={c("Sınırsız", "Unlimited")}
                            value={entry.durationSeconds}
                            onChange={(event) =>
                              update({ durationSeconds: event.target.value })
                            }
                          />
                        </label>
                        <Checkbox
                          checked={entry.enabled}
                          onChange={(event) =>
                            update({ enabled: event.target.checked })
                          }
                          label={c("Etkin", "Enabled")}
                        />
                      </article>
                    );
                  })}
                </div>
              </section>
            ) : null}
            <Button
              variant="primary"
              disabled={
                !endpointId ||
                !eventEntries.some((entry) => entry.enabled) ||
                new Date(endsAt) <= new Date(startsAt)
              }
              loading={createEvent.isPending}
              onClick={() => createEvent.mutate()}
            >
              {c("Etkinlik yayınını planla", "Schedule event broadcast")}
            </Button>
          </Card>
        </div>
      ) : null}

      {tab === "live" ? (
        <div className="display-workflow__live">
          {(events.data?.items ?? []).length ? (
            events.data!.items.map((item) => (
              <article key={item.id}>
                <header>
                  <div>
                    <Badge
                      tone={
                        item.status === "running"
                          ? "success"
                          : item.status === "paused"
                            ? "warning"
                            : "neutral"
                      }
                    >
                      {item.status}
                    </Badge>
                    <h3>{item.name}</h3>
                    <p>
                      {item.entries.length} {c("oyun", "games")} ·{" "}
                      {item.controlMode === "automatic"
                        ? c("Otomatik yönetim", "Automatic control")
                        : item.controlMode === "manual"
                          ? c("Moderatör yönetimi", "Moderator control")
                          : c(
                              "Otomatik + moderatör",
                              "Automatic + moderator",
                            )}{" "}
                      ·{" "}
                      {item.rotationMode === "sequential"
                        ? c("Sırayla", "Sequential")
                        : item.rotationMode === "shuffle"
                          ? c("Karışık", "Shuffle")
                          : c("Oyuncu oylaması", "Player voting")}
                    </p>
                  </div>
                  <strong>
                    {item.entries.find(
                      (entry) => entry.id === item.currentEntryId,
                    )?.variant?.name ?? c("Oyun bekliyor", "Waiting for game")}
                  </strong>
                </header>
                <div className="display-workflow__controls">
                  <Button
                    size="sm"
                    onClick={() =>
                      control.mutate({ eventId: item.id, action: "start" })
                    }
                  >
                    {c("Başlat", "Start")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      control.mutate({
                        eventId: item.id,
                        action: item.status === "paused" ? "resume" : "pause",
                      })
                    }
                  >
                    {item.status === "paused"
                      ? c("Sürdür", "Resume")
                      : c("Duraklat", "Pause")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      control.mutate({ eventId: item.id, action: "next" })
                    }
                  >
                    {c("Sonraki oyun", "Next game")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      control.mutate({
                        eventId: item.id,
                        action: "release-force",
                      })
                    }
                  >
                    {c("Otomatik akışa dön", "Return to automatic flow")}
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      control.mutate({ eventId: item.id, action: "stop" })
                    }
                  >
                    {c("Bitir", "Stop")}
                  </Button>
                </div>
                <div className="display-workflow__force">
                  {item.entries.map((entry) => (
                    <button
                      type="button"
                      key={entry.id}
                      onClick={() =>
                        control.mutate({
                          eventId: item.id,
                          action: "jump",
                          targetEntryId: entry.id,
                        })
                      }
                    >
                      <span>{entry.variant?.name}</span>
                      <small>
                        {entry.id === item.currentEntryId
                          ? c("Şimdi oynuyor", "Now playing")
                          : c("Bu oyuna geç", "Switch to game")}
                      </small>
                    </button>
                  ))}
                </div>
              </article>
            ))
          ) : (
            <EmptyState
              title={c(
                "Planlanmış etkinlik yayını yok",
                "No scheduled event broadcast",
              )}
              description={c(
                "Etkinliği hazırla adımından ilk yayınınızı planlayın.",
                "Schedule your first broadcast from the Prepare event step.",
              )}
            />
          )}
        </div>
      ) : null}
    </section>
  );
}
